import {
    AlertTriangle,
    ChevronDown,
    Loader2,
    RotateCw,
    Search,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import {
    TokenIcon,
    useTokenPairs,
    useTokenPairStore,
    useVolumeOrder,
} from '@/features/tokenPairs';
import { cn } from '@/lib/utils';

import { useFavoriteMarkets } from '../hooks/useFavoriteMarkets';
import { selectHeldCoins, useTradingStore } from '../store';

import { MarketRow } from './MarketRow';

/**
 * Market picker for widths below the markets sidebar's 1024px gate: a bottom
 * sheet with search and favorites, which a dropdown over ~230 markets can't
 * give you on a phone. Selecting is never blocked by an open position: one
 * position per coin is allowed, so switching is how you reach the next market
 * to trade. Markets you already hold are marked, not disabled.
 *
 * The trigger deliberately mirrors `TokenPairSelect iconOnly` — borderless
 * base-token logo + symbol — so the chart-header strip reads identically
 * whichever picker is mounted.
 */
const MobileMarketSheet: React.FC<{ className?: string }> = ({ className }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    const setSelectedCoin = useTokenPairStore((s) => s.setSelectedCoin);
    const networkId = useTokenPairStore((s) => s.networkId);
    // Snapshot prices only need to be reasonably fresh, and only while the
    // sheet can be opened.
    const { data, isLoading, isError, refetch } = useTokenPairs(networkId, {
        refetchInterval: 30_000,
    });
    // see MarketsSidebar: switching is no longer locked by an open position,
    // it is how you reach the next market to trade.
    const heldCoins = useTradingStore(selectHeldCoins);
    const { favorites, toggleFavorite } = useFavoriteMarkets();

    // Busiest market first. The ranking is held still across the 30s refetch —
    // see useVolumeOrder for why a live re-sort is a mis-click risk here.
    const byVolume = useVolumeOrder(data?.markets ?? []);

    const markets = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = q
            ? byVolume.filter((m) => m.coin.toLowerCase().includes(q))
            : byVolume;
        if (favorites.length === 0) return filtered;
        // Pinned markets first; sort() is stable, so the volume order holds
        // within each group.
        const pinned = new Set(favorites);
        return [...filtered].sort(
            (a, b) => Number(pinned.has(b.coin)) - Number(pinned.has(a.coin)),
        );
    }, [byVolume, query, favorites]);

    return (
        <Drawer
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (next) setQuery('');
            }}
        >
            <DrawerTrigger asChild>
                {/* The trigger carries the outage the way TokenPairSelect's
                    "Pairs unavailable" state does at 1024px+: without it a
                    markets outage on a phone is invisible unless the user
                    happens to open the sheet. The selected symbol stays put
                    (the chart is still streaming it) and the sheet still
                    opens, because that is where the Retry lives. */}
                <button
                    type="button"
                    aria-label={
                        isError
                            ? 'Markets unavailable, open to retry'
                            : 'Select market'
                    }
                    title={isError ? 'Markets unavailable' : undefined}
                    className={cn(
                        'flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-1 transition-colors hover:bg-secondary/60',
                        isError &&
                            'border border-destructive/30 bg-destructive/5',
                        className,
                    )}
                >
                    <TokenIcon token={selectedCoin.toLowerCase()} />
                    <span className="font-mono text-xs font-bold text-foreground">
                        {selectedCoin.toUpperCase()}
                    </span>
                    {isError ? (
                        <AlertTriangle
                            size={13}
                            className="shrink-0 text-destructive"
                        />
                    ) : (
                        <ChevronDown
                            size={13}
                            className="text-muted-foreground"
                        />
                    )}
                </button>
            </DrawerTrigger>
            <DrawerContent>
                <DrawerHeader className="pb-2">
                    <DrawerTitle>Markets</DrawerTitle>
                </DrawerHeader>

                {/* Search — 44px tall for thumbs, unlike the sidebar's 40px */}
                <div className="mx-4 flex items-center gap-2 rounded-xl border border-border bg-input px-3 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/40">
                    <Search
                        size={14}
                        className="shrink-0 text-muted-foreground"
                    />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        aria-label="Search markets"
                        placeholder="Search markets"
                        className="h-11 w-full border-0 bg-transparent p-0 text-sm text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-0 focus:outline-none focus:ring-0"
                    />
                </div>

                <div className="mt-2 max-h-[55vh] overflow-y-auto pb-6">
                    {isLoading && (
                        <div className="flex items-center justify-center py-8">
                            <Loader2
                                size={16}
                                className="animate-spin text-muted-foreground"
                            />
                        </div>
                    )}
                    {/* Same condition and wording as TokenPairSelect's desktop
                        affordance (same shared query), so the two pickers never
                        disagree about whether markets are up. Rows below may
                        still be showing the last good snapshot, which is why
                        this reads as an alert rather than a muted note. */}
                    {isError && (
                        <div
                            role="alert"
                            className="mx-4 flex flex-col items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-6 text-center"
                        >
                            <AlertTriangle
                                size={16}
                                className="shrink-0 text-destructive"
                            />
                            <span className="font-mono text-xs text-destructive">
                                Markets unavailable
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                                Prices below may be out of date.
                            </span>
                            <button
                                type="button"
                                onClick={() => void refetch()}
                                className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-destructive/30 px-3 py-1.5 text-xs text-destructive transition-colors hover:bg-destructive/10"
                            >
                                <RotateCw size={11} />
                                Retry
                            </button>
                        </div>
                    )}
                    {!isLoading && !isError && markets.length === 0 && (
                        <p
                            role="status"
                            className="px-4 py-6 text-center text-xs text-muted-foreground"
                        >
                            No market matches "{query}".
                        </p>
                    )}
                    {markets.map((market) => (
                        <MarketRow
                            key={market.coin}
                            market={market}
                            isSelected={
                                market.coin.toLowerCase() ===
                                selectedCoin.toLowerCase()
                            }
                            held={heldCoins.has(market.coin.toUpperCase())}
                            onSelect={() => {
                                setSelectedCoin(market.coin);
                                setOpen(false);
                            }}
                            favorite={favorites.includes(market.coin)}
                            onToggleFavorite={() => toggleFavorite(market.coin)}
                        />
                    ))}
                </div>
            </DrawerContent>
        </Drawer>
    );
};

export default MobileMarketSheet;
